import { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, ShoppingCart, TrendingUp, Users, CreditCard, 
  Banknote, Smartphone, RefreshCw, Clock, BarChart3, Bell, Truck, Package, 
  ArrowRight, AlertTriangle, FileText, Wallet, ClipboardList
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ExpiryAlertCard } from '@/components/shared/ExpiryAlertCard';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '@/utils/dateFormat';
import { posAPI, stockTransfersAPI } from '@/services/api';
import api from '@/services/api';

const statusConfig = {
  new: { label: 'New', color: 'bg-purple-100 text-purple-700' },
  processing: { label: 'Processing', color: 'bg-amber-100 text-amber-700' },
  in_transit: { label: 'In Transit', color: 'bg-blue-100 text-blue-700' },
  received: { label: 'Received', color: 'bg-emerald-100 text-emerald-700' }
};

const StoreDashboard = () => {
  const [stats, setStats] = useState(null);
  const [transferAlerts, setTransferAlerts] = useState(null);
  const [incomingReceipts, setIncomingReceipts] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const storeId = user?.entity_id;

  const fetchStats = useCallback(async () => {
    if (!storeId) return;
    
    setLoading(true);
    try {
      const response = await posAPI.getDashboardStats(storeId);
      setStats(response.data);
      
      // Fetch transfer alerts
      try {
        const alertsRes = await stockTransfersAPI.getAlerts(storeId, 'store');
        setTransferAlerts(alertsRes.data);
      } catch (e) {
        console.log('Transfer alerts not available:', e);
      }
      // Fetch incoming stock receipts (dispatched to this store, not yet received)
      try {
        const inRes = await api.get('/transfers/store/alerts/incoming-receipts', { params: { limit: 10 } });
        setIncomingReceipts(inRes.data || { count: 0, items: [] });
      } catch (e) {
        console.log('Incoming receipts API not available:', e);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch dashboard stats', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [storeId, toast]);

  useEffect(() => {
    fetchStats();
    // Refresh stats every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const quickActions = [
    { 
      label: 'Start Billing', 
      icon: ShoppingCart, 
      onClick: () => navigate('/store/pos'),
      color: 'bg-emerald-500 hover:bg-emerald-600',
      disabled: !stats?.register?.is_open
    },
    { 
      label: stats?.register?.is_open ? 'Close Register' : 'Open Register', 
      icon: DollarSign, 
      onClick: () => navigate('/store/register'),
      color: stats?.register?.is_open ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-500 hover:bg-blue-600'
    },
    { 
      label: 'Sales History', 
      icon: BarChart3, 
      onClick: () => navigate('/store/sales'),
      color: 'bg-purple-500 hover:bg-purple-600'
    },
  ];

  const widgets = stats?.widgets || {};

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="store-dashboard">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Store Dashboard</h1>
          <p className="text-gray-500">{user?.entity_name} - {formatDate(new Date())}</p>
        </div>
        <Button variant="outline" onClick={fetchStats} className="border-gray-300">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <ExpiryAlertCard scope="store" reportPath="/store/reports/expiry" />

      {/* Incoming Stock Receipts — dispatched to this store, pending receipt */}
      {incomingReceipts.count > 0 && (
        <div
          onClick={() => navigate('/store/stock-receipts')}
          className="mb-6 rounded-xl border-l-4 border-emerald-500 bg-emerald-50 hover:bg-emerald-100 shadow-sm p-4 cursor-pointer transition-colors"
          data-testid="incoming-receipts-card"
        >
          <div className="flex items-start gap-3">
            <Truck className="w-6 h-6 mt-0.5 shrink-0 text-emerald-600" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-emerald-800">
                {incomingReceipts.count} stock receipt{incomingReceipts.count > 1 ? 's' : ''} arrived / in-transit
              </p>
              <ul className="mt-1 text-sm space-y-0.5 text-emerald-700">
                {incomingReceipts.items.slice(0, 5).map(r => {
                  const dispMs = new Date(r.dispatched_at).getTime();
                  const h = Math.max(0, Math.floor((Date.now() - dispMs) / 60000));
                  const hh = Math.floor(h / 60);
                  const age = hh >= 24 ? `${Math.floor(hh / 24)}d ${hh % 24}h ago` : hh >= 1 ? `${hh}h ${h % 60}m ago` : `${h}m ago`;
                  const statusChip = r.status === 'delivered'
                    ? { label: 'Delivered — ready to receive', color: 'bg-emerald-200 text-emerald-900' }
                    : r.status === 'in_transit'
                    ? { label: 'In Transit', color: 'bg-blue-100 text-blue-700' }
                    : { label: 'Dispatched', color: 'bg-indigo-100 text-indigo-700' };
                  return (
                    <li key={r.id} className="truncate">
                      <span className="font-mono text-xs">{r.transfer_number}</span>
                      <span className="opacity-70"> from </span>
                      <span className="font-semibold">{r.source_name}</span>
                      <span className={`ml-1 text-xs px-1.5 py-0.5 rounded font-medium ${statusChip.color}`}>{statusChip.label}</span>
                      <span className="ml-1 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">{age}</span>
                      {r.vehicle_number && (
                        <span className="opacity-70"> · 🚚 {r.vehicle_number}</span>
                      )}
                      <span className="opacity-70"> · {r.total_items} item{r.total_items !== 1 ? 's' : ''}</span>
                    </li>
                  );
                })}
                {incomingReceipts.count > 5 && (
                  <li className="text-xs opacity-75">… and {incomingReceipts.count - 5} more</li>
                )}
              </ul>
              <p className="text-xs opacity-75 mt-2">Click to open Stock Receipts</p>
            </div>
            <Badge className="bg-emerald-600 text-white" data-testid="incoming-receipts-count">
              {incomingReceipts.count}
            </Badge>
          </div>
        </div>
      )}

      {/* Register Status Banner */}
      {stats && (
        <div className={`mb-6 p-4 rounded-lg ${stats.register?.is_open ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${stats.register?.is_open ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`} />
              <div>
                <p className={`font-semibold ${stats.register?.is_open ? 'text-green-800' : 'text-amber-800'}`}>
                  {stats.register?.is_open ? 'Cash Register Open' : 'Cash Register Closed'}
                </p>
                {stats.register?.is_open && (
                  <p className="text-sm text-green-600">
                    Counter: {stats.register.counter_id} | Shift: {stats.register.shift_id} | Opened by: {stats.register.opened_by}
                  </p>
                )}
              </div>
            </div>
            {!stats.register?.is_open && (
              <Button 
                onClick={() => navigate('/store/register')}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                Open Register
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Transfer Alert Banner */}
      {transferAlerts && transferAlerts.in_transit_incoming > 0 && (
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold">{transferAlerts.in_transit_incoming} Stock Transfer(s) Arriving</p>
              <p className="text-sm text-white/80">
                Click to view and receive incoming stock
              </p>
            </div>
          </div>
          <Button 
            onClick={() => navigate('/store/transfers')}
            className="bg-white text-blue-600 hover:bg-white/90"
          >
            <Package className="w-4 h-4 mr-2" />
            Receive Stock
          </Button>
        </div>
      )}

      {/* NEW: Alert & Action Widgets */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="dashboard-widgets">
          {/* Pending Stock Transfers */}
          <Card 
            className={`bg-white border-gray-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${widgets.pending_stock_transfers > 0 ? 'border-l-4 border-l-blue-500' : ''}`}
            onClick={() => navigate('/store/incoming-transfers')}
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Incoming Transfers</p>
                  <p className={`text-2xl font-bold ${widgets.pending_stock_transfers > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                    {widgets.pending_stock_transfers || 0}
                  </p>
                </div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${widgets.pending_stock_transfers > 0 ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <Truck className={`w-5 h-5 ${widgets.pending_stock_transfers > 0 ? 'text-blue-600' : 'text-gray-400'}`} />
                </div>
              </div>
              {widgets.pending_stock_transfers > 0 && (
                <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                  <Bell className="w-3 h-3" /> Awaiting receipt
                </p>
              )}
            </CardContent>
          </Card>

          {/* Pending Franchise Requests (for owned stores) */}
          {!stats.is_franchise && (
            <Card 
              className={`bg-white border-gray-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${widgets.pending_franchise_requests > 0 ? 'border-l-4 border-l-purple-500' : ''}`}
              onClick={() => navigate('/store/franchise-supply')}
            >
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Franchise Requests</p>
                    <p className={`text-2xl font-bold ${widgets.pending_franchise_requests > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                      {widgets.pending_franchise_requests || 0}
                    </p>
                  </div>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${widgets.pending_franchise_requests > 0 ? 'bg-purple-100' : 'bg-gray-100'}`}>
                    <ClipboardList className={`w-5 h-5 ${widgets.pending_franchise_requests > 0 ? 'text-purple-600' : 'text-gray-400'}`} />
                  </div>
                </div>
                {widgets.pending_franchise_requests > 0 && (
                  <p className="text-xs text-purple-600 mt-2 flex items-center gap-1">
                    <Bell className="w-3 h-3" /> Pending fulfillment
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Low Stock Products */}
          <Card 
            className={`bg-white border-gray-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${widgets.low_stock_products > 0 ? 'border-l-4 border-l-amber-500' : ''}`}
            onClick={() => navigate('/store/low-stock')}
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Low Stock Items</p>
                  <p className={`text-2xl font-bold ${widgets.low_stock_products > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                    {widgets.low_stock_products || 0}
                  </p>
                </div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${widgets.low_stock_products > 0 ? 'bg-amber-100' : 'bg-gray-100'}`}>
                  <AlertTriangle className={`w-5 h-5 ${widgets.low_stock_products > 0 ? 'text-amber-600' : 'text-gray-400'}`} />
                </div>
              </div>
              {widgets.low_stock_products > 0 && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <Bell className="w-3 h-3" /> Needs reorder
                </p>
              )}
            </CardContent>
          </Card>

          {/* Pending GRN Entries */}
          <Card 
            className={`bg-white border-gray-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${widgets.pending_grn_entries > 0 ? 'border-l-4 border-l-indigo-500' : ''}`}
            onClick={() => navigate('/store/grn')}
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Pending GRN</p>
                  <p className={`text-2xl font-bold ${widgets.pending_grn_entries > 0 ? 'text-indigo-600' : 'text-gray-400'}`}>
                    {widgets.pending_grn_entries || 0}
                  </p>
                </div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${widgets.pending_grn_entries > 0 ? 'bg-indigo-100' : 'bg-gray-100'}`}>
                  <FileText className={`w-5 h-5 ${widgets.pending_grn_entries > 0 ? 'text-indigo-600' : 'text-gray-400'}`} />
                </div>
              </div>
              {widgets.pending_grn_entries > 0 && (
                <p className="text-xs text-indigo-600 mt-2 flex items-center gap-1">
                  <Bell className="w-3 h-3" /> Process received stock
                </p>
              )}
            </CardContent>
          </Card>

          {/* Today's Expenses */}
          <Card 
            className="bg-white border-gray-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/store/expenses')}
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Today's Expenses</p>
                  <p className="text-2xl font-bold text-red-600">
                    ₹{(widgets.todays_expenses || 0).toLocaleString()}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-red-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Click to manage</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {quickActions.map((action, idx) => (
          <Button
            key={idx}
            onClick={action.onClick}
            disabled={action.disabled}
            className={`h-20 ${action.color} text-white flex items-center justify-center gap-3 text-lg font-semibold`}
          >
            <action.icon className="w-6 h-6" />
            {action.label}
          </Button>
        ))}
      </div>

      {/* Stats Cards */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading statistics...</div>
      ) : stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Today&apos;s Sales</p>
                  <p className="text-2xl font-bold text-emerald-600">₹{stats.today?.total_sales?.toLocaleString() || 0}</p>
                  <p className="text-xs text-gray-400 mt-1">{stats.today?.total_transactions || 0} transactions</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Cash Sales</p>
                  <p className="text-2xl font-bold text-green-600">₹{stats.today?.cash_sales?.toLocaleString() || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Banknote className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Card Sales</p>
                  <p className="text-2xl font-bold text-blue-600">₹{stats.today?.card_sales?.toLocaleString() || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">UPI Sales</p>
                  <p className="text-2xl font-bold text-purple-600">₹{stats.today?.upi_sales?.toLocaleString() || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Additional Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-gray-700 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                Average Bill Value
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-800">
                ₹{stats.today?.average_bill?.toLocaleString() || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-gray-700 flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" />
                Held Bills
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-amber-600">
                {stats.held_bills_count || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">Pending completion</p>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-gray-700 flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-500" />
                Returns Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-red-600">
                {stats.today?.returns_count || 0}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Register Balance Info */}
      {stats?.register?.is_open && (
        <Card className="bg-emerald-50 border-emerald-200 mt-6">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-800 font-semibold">Current Register Balance</p>
                <p className="text-sm text-emerald-600">
                  Opening: ₹{stats.register.opening_balance?.toLocaleString()} + Cash Sales: ₹{stats.today?.cash_sales?.toLocaleString() || 0}
                </p>
              </div>
              <p className="text-2xl font-bold text-emerald-700">
                ₹{((stats.register.opening_balance || 0) + (stats.today?.cash_sales || 0)).toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StoreDashboard;
